import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import AvatarUploader from '../components/AvatarUploader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import { Star, Flag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingReviewId, setReportingReviewId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserStats();
      fetchUserReviews();
    }
  }, [user]);

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const fetchUserReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${user.id}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    if (reportReason.trim().length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters)');
      return;
    }

    try {
      setReporting(true);
      await axios.post(
        `${API}/reviews/${reportingReviewId}/report`,
        { reason: reportReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review reported successfully');
      setReportModalOpen(false);
      setReportReason('');
      setReportingReviewId(null);
      fetchUserReviews(); // Refresh reviews
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report review');
    } finally {
      setReporting(false);
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'GOLD':
        return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/40';
      case 'SILVER':
        return 'bg-gray-400/20 text-gray-400 border-gray-400/40';
      case 'BRONZE':
        return 'bg-orange-600/20 text-orange-600 border-orange-600/40';
      default:
        return '';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <header className="bg-brand-surface border-b border-zinc-800 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.svg" alt="Sale Sphere" className="h-7 w-7" />
              <div>
                <h1 className="font-display text-xl text-brand-text tracking-wide">Profile Settings</h1>
                <p className="text-sm text-brand-muted">Manage your account</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(user.role === 'BUSINESS' ? '/business/dashboard' : '/salesperson/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6">
          {/* Avatar Section */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Photo</CardTitle>
              <CardDescription>
                Upload a square photo. It will be used across the platform.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AvatarUploader />
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-brand-muted">Name</label>
                <p className="text-brand-text">{user.name}</p>
              </div>

              {user.role === 'BUSINESS' && (
                <BusinessNameField token={token} />
              )}

              <div>
                <label className="text-sm font-medium text-brand-muted">Email</label>
                <p className="text-brand-text">{user.email}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-brand-muted">Role</label>
                <div className="mt-1">
                  <Badge variant={user.role === 'BUSINESS' ? 'default' : 'secondary'}>
                    {user.role}
                  </Badge>
                </div>
              </div>

              {(user.city || user.region || user.country) && (
                <div>
                  <label className="text-sm font-medium text-brand-muted">Location</label>
                  <p className="text-brand-text">
                    {[user.city, user.region, user.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reviews Section */}
          {stats && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Reviews & Ratings</CardTitle>
                    <CardDescription>What others say about working with you</CardDescription>
                  </div>
                  {stats.badge && (
                    <Badge className={getBadgeColor(stats.badge)} variant="outline">
                      {stats.badge}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats Summary */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-brand-bg rounded-lg border border-zinc-800">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Star className="w-5 h-5 fill-brand-gold text-brand-gold" />
                      <span className="text-2xl font-bold text-brand-gold">
                        {stats.average_rating.toFixed(1)}
                      </span>
                    </div>
                    <p className="text-xs text-brand-muted">Avg Rating</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-brand-text mb-1">
                      {stats.total_reviews}
                    </div>
                    <p className="text-xs text-brand-muted">Total Reviews</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-brand-text mb-1">
                      {stats.recent_reviews}
                    </div>
                    <p className="text-xs text-brand-muted">Recent (6mo)</p>
                  </div>
                </div>

                {/* Reviews List */}
                {loading ? (
                  <div className="text-center py-8 text-brand-muted">Loading reviews...</div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8 text-brand-muted">
                    No reviews yet. Complete some partnerships to get reviewed!
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 bg-brand-bg rounded-lg border border-zinc-800"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={16}
                                    className={
                                      star <= review.rating
                                        ? 'fill-brand-gold text-brand-gold'
                                        : 'text-gray-600'
                                    }
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium text-brand-text">
                                {review.from_user_name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {review.from_user_role}
                              </Badge>
                            </div>
                            <p className="text-xs text-brand-muted">
                              {new Date(review.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!review.reported && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setReportingReviewId(review.id);
                                setReportModalOpen(true);
                              }}
                              className="text-brand-muted hover:text-red-500"
                            >
                              <Flag size={14} />
                            </Button>
                          )}
                        </div>
                        {review.comment && (
                          <p className="text-sm text-brand-text mt-2">{review.comment}</p>
                        )}
                        {review.reported && (
                          <Badge variant="outline" className="mt-2 text-xs text-red-500 border-red-500/20">
                            Reported
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Account Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                onClick={logout}
                className="w-full"
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Report Review Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for reporting</Label>
              <Textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Please provide details about why this review should be reviewed..."
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-brand-muted mt-1">
                {reportReason.length}/500 characters (minimum 10 required)
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setReportModalOpen(false);
                  setReportReason('');
                  setReportingReviewId(null);
                }}
                className="flex-1"
                disabled={reporting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReport}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={reporting || reportReason.trim().length < 10}
              >
                {reporting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Business Name Field Component
const BusinessNameField = ({ token }) => {
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [businessName, setBusinessName] = useState(user?.business_name || '');
  const [saving, setSaving] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const API = `${BACKEND_URL}/api`;

  // Don't render if user is not loaded
  if (!user) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.patch(
        `${API}/auth/profile`,
        { business_name: businessName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update user in context
      setUser({ ...user, business_name: businessName });
      
      toast.success('Business name updated!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating business name:', error);
      toast.error(error.response?.data?.detail || 'Failed to update business name');
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div>
        <label className="text-sm font-medium text-brand-muted">Business Name</label>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-brand-text">{user?.business_name || 'Not set'}</p>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => setIsEditing(true)}
            className="h-7 text-xs"
          >
            Edit
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor="business-name">Business Name</Label>
      <div className="flex gap-2 mt-1">
        <input
          id="business-name"
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
          placeholder="Enter your business name"
        />
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="sm"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button 
          onClick={() => {
            setBusinessName(user?.business_name || '');
            setIsEditing(false);
          }} 
          variant="outline"
          size="sm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};


export default Profile;
